import { OIDCClient } from "../apiclient/oidc-client";
import * as HEADERS from "../apiclient/common";

import MixpanelTracker from "./common/mixpanel-tracker";
import { installGlobalErrorTracking } from "./common/error-tracking";
import { fundwavePatterns, RecalculateScope } from "./common/constants";

import { FetchQueue } from "@fundwave/fetchq";
import VersionHelper from "../transformer/version-helper";

export default class ClientUtil {
  static init() {
    // update local storage
    localStorage.setItem("client", "excel");
    localStorage.setItem("recordingEvents", "false");

    MixpanelTracker.trackEvent("session_init", {});
    installGlobalErrorTracking();

    const fetchQueue = new FetchQueue({
      concurrent: 5,
      queuingPatterns: fundwavePatterns,
      pre: [
        {
          pattern: fundwavePatterns,
          hook: async (_url, options) => {
            const method = options?.method?.toUpperCase() === "POST" ? "POST_HEADERS" : "GET_HEADERS";
            const defaultHeaders = HEADERS[method];
            const headers = await OIDCClient.prepareHeaders(defaultHeaders);
            Object.assign(options?.headers || {}, headers);
          }
        }
      ],
    });

    (window as any).fetchQueue = fetchQueue;
    (global as any).fetch = fetchQueue.getFetchMethod();

    Office.onReady(async () => {
      VersionHelper.loadDialog();
      MixpanelTracker.batchMultipleEvents();

      await Excel.run({ delayForCellEdit: true }, async (context) => {
        context.workbook.load("name");
        await context.sync();
        localStorage.setItem("metadata", JSON.stringify(Office.context.diagnostics || {}));
        localStorage.setItem("workbook", context.workbook.name);
      });
    });
  }

  static getTokenDetails() {
    const token = sessionStorage.getItem("token");
    const tokenInBytes = atob(token?.split(".")[1] || "");
    if (!tokenInBytes) return;
    return JSON.parse(tokenInBytes);
  }

  static async recalculateCells(scope: RecalculateScope = "sheet"): Promise<{ msg: string }> {
    if (localStorage.getItem("client") !== "excel") return { msg: "Dummy method" };

    // Worksheet.calculate() needs ExcelApi 1.6; older hosts accepted by the manifest don't have it.
    if (!Office.context.requirements.isSetSupported("ExcelApi", "1.6")) {
      return { msg: "Recalculate isn't supported on this version of Excel" };
    }
    MixpanelTracker.trackEvent("recalculate_cells", { scope });
    return Excel.run({ delayForCellEdit: true }, async (context) => {
      if (scope === "sheet") {
        const sheet = context.workbook.worksheets.getActiveWorksheet();

        // markAllDirty: true forces a full recalc of this sheet, not just cells Excel thinks are
        // dirty, so formulas depending on other sheets are resolved correctly too.
        sheet.calculate(true);
        await context.sync();

        return { msg: "Sheet recalculated successfully" };
      }

      context.workbook.application.calculate(Excel.CalculationType.full);
      await context.sync();

      return { msg: "Workbook recalculated successfully" };
    });
  }
}

document.addEventListener("init", () => ClientUtil.init());
